import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Search,
  LayoutGrid,
  List,
  SlidersHorizontal,
  ChevronDown,
  FileText,
  MoreVertical,
  Ban,
  FolderInput,
  History,
  Trash2,
  Inbox,
  Send,
  CheckCircle2,
  AlertCircle,
  User,
  Users,
  Star,
  Layers,
  FileStack,
  Clock,
  Copy,
} from "lucide-react";
import {
  addDocument,
  deleteTemplate,
  getTemplates,
  addTemplate,
  voidEnvelope,
  moveEnvelope,
  getEnvelopeHistory,
  viewDocument,
} from "../../api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const CATEGORIES = [
  "Human Resources",
  "Legal",
  "Finance",
  "Sales",
  "Onboarding",
  "Procurement",
];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sept", "Oct", "Nov", "Dec",
];
const fmtStamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};
const stampLine = (doc, option) => {
  if (option === "sent") {
    const s = fmtStamp(doc.created_at);
    return s ? `Sent ${s}` : null;
  }
  if (option === "completed") {
    const s = fmtStamp(doc.signed_at || doc.created_at);
    return s ? `Completed ${s}` : null;
  }
  // inbox / action_required
  const s = fmtStamp(doc.created_at);
  return s ? `Received ${s}` : null;
};

const SECTIONS = [
  { key: "agreements", label: "Agreements" },
  { key: "templates", label: "Templates" },
  { key: "reports", label: "Reports" },
];

const OPTIONS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "sent", label: "Sent", icon: Send },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
  { key: "action_required", label: "Action Required", icon: AlertCircle },
];

const TEMPLATE_OPTIONS = [
  { key: "me", label: "My Templates", icon: User },
  { key: "shared", label: "Shared With Me", icon: Users },
  { key: "favorites", label: "Favorites", icon: Star },
  { key: "all", label: "All Templates", icon: Layers },
];

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const DocumentCard = ({ doc, option, isTemplate, onPreview, onVoid, onMove, onHistory, onDeleteTemplate }) => {
  const stamp = !isTemplate ? stampLine(doc, option) : null;
  const isVoided = doc.status === "voided";
  const isCompleted =
    doc.status === "completed" ||
    (doc.progress && doc.progress.total > 0 && doc.progress.signed === doc.progress.total);
  const canVoid =
    doc.direction === "sent" &&
    !isVoided &&
    doc.status !== "declined" &&
    !isCompleted;
  // Unread = a document this recipient received but hasn't opened yet (per-user).
  const unread = !isTemplate && doc.direction === "received" && !doc.viewed;

  return (
  <motion.div
    variants={cardVariants}
    transition={{ duration: 0.32, ease: "easeOut" }}
    data-testid={`document-card-${doc.id}`}
    data-unread={unread ? "true" : "false"}
    onClick={() => onPreview(doc)}
    className={`group relative cursor-pointer rounded-2xl p-4 transition-colors duration-200 ${
      unread
        ? "bg-[#EFE3D6] ring-1 ring-[#1A1815]/10 hover:bg-[#E9DBCB] dark:bg-white/[0.11] dark:ring-white/10 dark:hover:bg-white/[0.15]"
        : "bg-[#F6EFE8] hover:bg-[#F0EBE5] dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
    }`}>
    {unread && (
      <span
        data-testid={`unread-dot-${doc.id}`}
        title="Unopened"
        className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-[#F79009] ring-2 ring-[#EFE3D6] dark:ring-[#2A2433]"
      />
    )}
    <div className="flex items-center gap-3">
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${doc.color}33`, color: doc.color }}
      >
        <FileText className="h-6 w-6" strokeWidth={1.6} />
      </div>
      <div className="flex min-w-0 flex-col">
        <h3 className={`line-clamp-1 text-base text-[#1A1815] dark:text-white ${unread ? "font-extrabold" : "font-semibold"}`}>
          {doc.name}
        </h3>
        <span className="text-sm text-[#8B8680] dark:text-white/45">{doc.size}</span>
        {doc.sender_name && doc.direction === "received" && (
          <span className={`truncate text-xs ${unread ? "font-bold text-[#5A5550] dark:text-white/70" : "text-[#8B8680] dark:text-white/40"}`}>
            from {doc.sender_name}
          </span>
        )}
      </div>
      {isVoided ? (
        <span
          data-testid={`voided-badge-${doc.id}`}
          className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#E5484D]/15 px-2.5 py-1 text-[11px] font-bold text-[#B42318] dark:bg-[#E5484D]/20 dark:text-[#FF8A8A]"
        >
          <Ban className="h-3 w-3" strokeWidth={2.2} />
          Voided
        </span>
      ) : doc.status === "waiting" ? (
        <span
          data-testid={`waiting-badge-${doc.id}`}
          className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#EFE9E2] px-2.5 py-1 text-[11px] font-bold text-[#8B8680] dark:bg-white/10 dark:text-white/55"
        >
          <Clock className="h-3 w-3" strokeWidth={2.2} />
          Waiting
        </span>
      ) : doc.status === "action_required" ? (
        <span
          data-testid={`action-required-badge-${doc.id}`}
          className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#F79009]/15 px-2.5 py-1 text-[11px] font-bold text-[#B54708] dark:bg-[#F79009]/20 dark:text-[#FDB022]"
        >
          <AlertCircle className="h-3 w-3" strokeWidth={2.2} />
          Action Required
        </span>
      ) : doc.status === "pending" ? (
        <span
          data-testid={`pending-badge-${doc.id}`}
          className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#EFE9E2] px-2.5 py-1 text-[11px] font-bold text-[#8B8680] dark:bg-white/10 dark:text-white/55"
        >
          <Clock className="h-3 w-3" strokeWidth={2.2} />
          Awaiting others
        </span>
      ) : doc.status === "cc" ? (
        <span
          data-testid={`cc-badge-${doc.id}`}
          className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#EFE9E2] px-2.5 py-1 text-[11px] font-bold text-[#8B8680] dark:bg-white/10 dark:text-white/55"
        >
          <Copy className="h-3 w-3" strokeWidth={2.2} />
          Copy
        </span>
      ) : doc.status === "completed" ? (
        <span className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[#7AFFA1]/20 px-2.5 py-1 text-[11px] font-bold text-[#087443] dark:text-[#6EE7A8]">
          <CheckCircle2 className="h-3 w-3" strokeWidth={2.2} />
          Signed
        </span>
      ) : doc.favorite ? (
        <Star className="ml-auto h-4 w-4 fill-[#F79009] text-[#F79009]" strokeWidth={1.6} />
      ) : null}
    </div>

    {stamp && (
      <div
        data-testid={`doc-timestamp-${doc.id}`}
        className="mt-2 flex items-center gap-1.5 text-xs text-[#8B8680] dark:text-white/40"
      >
        <Clock className="h-3 w-3" strokeWidth={1.8} />
        {stamp}
      </div>
    )}

    {doc.direction === "sent" && !isVoided && doc.progress && doc.progress.total > 0 && (
      <div
        data-testid={`progress-line-${doc.id}`}
        className="mt-3 flex items-center gap-2 text-xs font-medium text-[#8B8680] dark:text-white/50"
      >
        <span className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-[#E7E0D8] dark:bg-white/10">
          <span
            className="h-full rounded-full bg-[#12B76A] transition-all"
            style={{ width: `${Math.round((doc.progress.signed / doc.progress.total) * 100)}%` }}
          />
        </span>
        <span className="shrink-0 whitespace-nowrap">
          {doc.progress.signed} of {doc.progress.total} signed
          {doc.progress.declined
            ? " · declined"
            : doc.progress.signed === doc.progress.total
            ? " · complete"
            : doc.progress.waiting_on
            ? ` · waiting on ${doc.progress.waiting_on}`
            : ""}
        </span>
      </div>
    )}

    <div className="mt-5 flex items-center justify-between">
      <div className="flex items-end gap-1.5">
        <span className="text-3xl font-bold tracking-tight text-[#1A1815] dark:text-white">
          {doc.pages}
        </span>
        <span className="mb-1 text-xs text-[#8B8680] dark:text-white/45">Pages</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-[#8B8680] dark:text-white/55">{doc.type}</span>
        {isTemplate ? (
          <button
            data-testid={`delete-item-${doc.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteTemplate(doc);
            }}
            title="Delete template"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#F2C0C0] hover:text-[#B42318] dark:bg-white/5 dark:text-white/50 dark:hover:bg-white/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`card-menu-btn-${doc.id}`}
                onClick={(e) => e.stopPropagation()}
                title="More options"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white/60 dark:hover:bg-white/10"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              className="w-44"
            >
              <DropdownMenuItem
                data-testid={`menu-void-${doc.id}`}
                disabled={!canVoid}
                onSelect={(e) => {
                  e.preventDefault();
                  if (canVoid) onVoid(doc);
                }}
                className="gap-2 text-[#B42318] focus:text-[#B42318] data-[disabled]:text-[#B0A99F]"
              >
                <Ban className="h-4 w-4" />
                Void
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  data-testid={`menu-move-${doc.id}`}
                  className="gap-2"
                >
                  <FolderInput className="h-4 w-4" />
                  Move
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-48">
                  {CATEGORIES.map((cat) => (
                    <DropdownMenuItem
                      key={cat}
                      data-testid={`menu-move-${cat.replace(/\s+/g, "-").toLowerCase()}-${doc.id}`}
                      onSelect={() => onMove(doc, cat)}
                      className={`gap-2 ${doc.category === cat ? "font-semibold" : ""}`}
                    >
                      {doc.category === cat && <CheckCircle2 className="h-3.5 w-3.5 text-[#12B76A]" />}
                      <span className={doc.category === cat ? "" : "pl-[22px]"}>{cat}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                data-testid={`menu-history-${doc.id}`}
                onSelect={() => onHistory(doc)}
                className="gap-2"
              >
                <History className="h-4 w-4" />
                History
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
    {doc.category && !isTemplate && (
      <div
        data-testid={`doc-category-${doc.id}`}
        className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#EFE9E2] px-2 py-0.5 text-[11px] font-medium text-[#8B8680] dark:bg-white/5 dark:text-white/45"
      >
        <FolderInput className="h-3 w-3" strokeWidth={1.8} />
        {doc.category}
      </div>
    )}
  </motion.div>
  );
};

const EnvelopeDocuments = ({ dark, onAction, documents = [], reloadDocs, company, user }) => {
  const navigate = useNavigate();
  // A sender always sees the tracking view for their own envelope. The recipient
  // fill-in view (/sign) is only for an actual recipient whose turn it is.
  const openDoc = (doc) => {
    // Mark the recipient's own copy as opened (read) — covers every open path
    // (sign view, tracking view, cc copies). Backend no-ops if already viewed.
    if (doc.direction === "received" && !doc.viewed) {
      viewDocument(doc.id).catch(() => {});
    }
    const target =
      doc.direction === "received" && doc.status === "action_required"
        ? `/sign/${doc.id}`
        : `/envelope/${doc.id}`;
    navigate(target, { state: { doc, company } });
  };
  const [section, setSection] = useState("agreements");
  const [activeOption, setActiveOption] = useState("inbox");
  const [activeTemplateOption, setActiveTemplateOption] = useState("me");
  const [view, setView] = useState("list");
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null); // { subject, events } | null
  const [historyLoading, setHistoryLoading] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [form, setForm] = useState({
    name: "",
    size: "1.2 MB",
    pages: 5,
    type: "PDF",
    status: "inbox",
    owner: "me",
    favorite: false,
  });

  const loadTemplates = () =>
    getTemplates(company.key)
      .then(setTemplates)
      .catch(() => onAction("Failed to load templates"));

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const isTemplates = section === "templates";
  const tabBg = dark ? "rgba(255,255,255,0.09)" : "#FFFFFF";

  // ----- Agreements filtering -----
  // Inbox = anything received (incoming) or explicitly filed as inbox.
  // Sent = anything the user sent or filed as sent. Received "action_required"
  // envelopes appear in BOTH Inbox and Action Required.
  const docMatch = (d, key) => {
    if (key === "inbox") return d.direction === "received" || d.status === "inbox";
    if (key === "sent") return d.direction === "sent" || d.status === "sent";
    if (key === "completed") return d.status === "completed";
    if (key === "action_required") return d.status === "action_required";
    return d.status === key;
  };
  const docCountBy = (key) => documents.filter((d) => docMatch(d, key)).length;
  // Most-recently-received first. For sequential signing, created_at is bumped to
  // when it became this recipient's turn, so "newest to act on" sorts to the top.
  const byRecent = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");
  const docsByOption = documents
    .filter((d) => docMatch(d, activeOption))
    .sort(byRecent);

  // ----- Reports data -----
  const reportData = [
    { name: "Inbox", value: docCountBy("inbox"), fill: "#DDCBF5" },
    { name: "Sent", value: docCountBy("sent"), fill: "#FFF87C" },
    { name: "Completed", value: docCountBy("completed"), fill: "#7AFFA1" },
    { name: "Action Required", value: docCountBy("action_required"), fill: "#F79009" },
  ];

  // ----- Templates filtering -----
  const tplMatch = (t, key) =>
    key === "all"
      ? true
      : key === "favorites"
      ? t.favorite
      : t.owner === key;
  const tplCountBy = (key) => templates.filter((t) => tplMatch(t, key)).length;
  const tplsByOption = templates.filter((t) => tplMatch(t, activeTemplateOption));

  // ----- Active view data -----
  const options = isTemplates ? TEMPLATE_OPTIONS : OPTIONS;
  const currentOption = isTemplates ? activeTemplateOption : activeOption;
  const setCurrentOption = isTemplates ? setActiveTemplateOption : setActiveOption;
  const countBy = isTemplates ? tplCountBy : docCountBy;
  const byOption = isTemplates ? tplsByOption : docsByOption;
  const filtered = byOption.filter(
    (d) =>
      d.name.toLowerCase().includes(query.toLowerCase()) &&
      (categoryFilter === "all" || d.category === categoryFilter)
  );
  const activeLabel = options.find((o) => o.key === currentOption)?.label;
  const fileCount = String(byOption.length).padStart(2, "0");

  const resetForm = () =>
    setForm({
      name: "",
      size: "1.2 MB",
      pages: 5,
      type: "PDF",
      status: "inbox",
      owner: "me",
      favorite: false,
    });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (isTemplates) {
        await addTemplate({
          name: form.name,
          size: form.size,
          pages: Number(form.pages) || 1,
          type: form.type,
          owner: form.owner,
          favorite: form.favorite,
          company: company.key,
        });
        await loadTemplates();
        onAction("Template added");
      } else {
        await addDocument({
          name: form.name,
          size: form.size,
          pages: Number(form.pages) || 1,
          type: form.type,
          status: form.status,
          company: company.key,
        });
        await reloadDocs();
        onAction("Document added");
      }
      setOpen(false);
      resetForm();
    } catch {
      onAction(isTemplates ? "Failed to add template" : "Failed to add document");
    }
  };

  const handleDeleteTemplate = async (item) => {
    try {
      await deleteTemplate(item.id);
      await loadTemplates();
      onAction(`Deleted "${item.name}"`);
    } catch {
      onAction("Failed to delete");
    }
  };

  const handleVoid = async (item) => {
    try {
      await voidEnvelope(item.id);
      await reloadDocs();
      onAction(`Voided "${item.name}"`);
    } catch (e) {
      onAction(e?.response?.data?.detail || "Failed to void envelope");
    } finally {
      setVoidTarget(null);
    }
  };

  const handleMove = async (item, category) => {
    if (item.category === category) return;
    try {
      await moveEnvelope(item.id, category);
      await reloadDocs();
      onAction(`Moved "${item.name}" to ${category}`);
    } catch (e) {
      onAction(e?.response?.data?.detail || "Failed to move document");
    }
  };

  const handleHistory = async (item) => {
    setHistoryLoading(true);
    setHistory({ subject: item.name, events: [] });
    try {
      const data = await getEnvelopeHistory(item.id);
      setHistory(data);
    } catch (e) {
      onAction(e?.response?.data?.detail || "Failed to load history");
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Folder tab strip - selected section moves to the big top-left slot */}
      <div className="relative flex items-end gap-[3px] overflow-x-auto">
        {[section, ...SECTIONS.map((s) => s.key).filter((k) => k !== section)].map(
          (key, idx) => {
            const s = SECTIONS.find((x) => x.key === key);
            const glide = { layout: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };
            if (idx === 0) {
              return (
                <motion.button
                  layout="position"
                  key={key}
                  transition={glide}
                  data-testid={`section-${key}`}
                  onClick={() => setSection(key)}
                  className="shrink-0 rounded-tl-[22px] pb-5 pl-6 pr-12 pt-5 text-left sm:pr-14 md:pl-8"
                  style={{
                    backgroundColor: tabBg,
                    clipPath:
                      "polygon(0 0, calc(100% - 54px) 0, calc(100% - 48px) 2px, calc(100% - 44px) 6px, calc(100% - 41px) 12px, 100% 100%, 0 100%)",
                  }}
                >
                  <h2 className="whitespace-nowrap text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white">
                    {s.label}
                  </h2>
                </motion.button>
              );
            }
            return (
              <motion.button
                layout="position"
                key={key}
                transition={glide}
                data-testid={`section-${key}`}
                onClick={() => setSection(key)}
                style={{ backgroundColor: tabBg }}
                className="mb-2 shrink-0 whitespace-nowrap rounded-t-[10px] px-4 pb-4 pt-2.5 text-sm font-medium text-[#8B8680] transition-colors duration-200 hover:text-[#1A1815] dark:text-white/45 dark:hover:text-white/75 sm:px-6"
              >
                {s.label}
              </motion.button>
            );
          }
        )}
      </div>

      {/* Panel body */}
      <div className="glass-panel -mt-2 flex flex-1 flex-col rounded-b-[22px] rounded-tl-none rounded-tr-[22px] px-6 pb-6 pt-2 md:px-8 md:pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={section}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="flex flex-1 flex-col"
          >
            {section === "reports" ? (
          <div className="mt-6 flex-1" data-testid="reports-view">
            {/* Summary chips */}
            <div className="flex flex-wrap gap-3">
              {[
                { label: "Total Agreements", value: documents.length, icon: FileStack },
                { label: "Templates", value: templates.length, icon: Layers },
                { label: "Completed", value: docCountBy("completed"), icon: CheckCircle2 },
              ].map(({ label, value, icon: Icon }) => (
                <div
                  key={label}
                  className="flex min-w-[150px] flex-1 items-center gap-3 rounded-2xl bg-[#F6EFE8] px-4 py-3.5 dark:bg-white/[0.04]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#1A1815] dark:bg-white/10 dark:text-white">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <div className="flex flex-col leading-tight">
                    <span className="text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white">
                      {value}
                    </span>
                    <span className="text-xs text-[#8B8680] dark:text-white/45">{label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Bar chart */}
            <div className="mt-6">
              <h3 className="mb-1 text-lg font-bold tracking-tight text-[#1A1815] dark:text-white">
                Agreements by status
              </h3>
              <p className="mb-4 text-sm text-[#8B8680] dark:text-white/45">
                Live breakdown across your pipeline
              </p>
              <div className="h-[300px] w-full" data-testid="reports-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={dark ? "rgba(255,255,255,0.08)" : "#EAE3DB"}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: dark ? "rgba(255,255,255,0.55)" : "#8B8680", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: dark ? "rgba(255,255,255,0.45)" : "#B0A99F", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)" }}
                      contentStyle={{
                        borderRadius: 14,
                        border: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E7E0D8",
                        background: dark ? "#1A1721" : "#FFFFFF",
                        color: dark ? "#fff" : "#1A1815",
                        fontFamily: "Urbanist, sans-serif",
                        fontWeight: 600,
                      }}
                    />
                    <Bar dataKey="value" radius={[10, 10, 0, 0]} maxBarSize={80}>
                      {reportData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Options row + controls */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2" data-testid="section-options">
                {options.map(({ key, label, icon: Icon }) => {
                  const isActive = currentOption === key;
                  return (
                    <button
                      key={key}
                      data-testid={`option-${key}`}
                      onClick={() => setCurrentOption(key)}
                      className={[
                        "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200",
                        isActive
                          ? "bg-[#1A1815] text-white dark:bg-white dark:text-black"
                          : "bg-[#F0EBE5] text-[#8B8680] hover:text-[#1A1815] dark:bg-white/5 dark:text-white/50 dark:hover:text-white/80",
                      ].join(" ")}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.8} />
                      <span className="hidden sm:inline">{label}</span>
                      <span
                        className={[
                          "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
                          isActive
                            ? "bg-white/20 text-white dark:bg-black/15 dark:text-black"
                            : "bg-white text-[#1A1815] dark:bg-white/10 dark:text-white/70",
                        ].join(" ")}
                      >
                        {countBy(key)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-3">
                <div className="flex min-w-[150px] items-center gap-2 border-b border-[#E7E0D8] px-1 py-2.5 dark:border-white/15">
                  <Search className="h-4 w-4 text-[#8B8680] dark:text-white/40" strokeWidth={1.8} />
                  <input
                    data-testid="search-files-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={isTemplates ? "Search templates" : "Search files"}
                    className="w-full bg-transparent text-sm text-[#1A1815] placeholder-[#B0A99F] outline-none dark:text-white dark:placeholder-white/35"
                  />
                </div>

                {/* Sliding view switch */}
                <div className="relative flex items-center rounded-full bg-[#F0EBE5] p-1 dark:bg-white/5">
                  <span
                    className={`pointer-events-none absolute left-1 top-1 h-9 w-9 rounded-full bg-white shadow-sm transition-transform duration-300 ease-out dark:bg-white/15 ${
                      view === "list" ? "translate-x-9" : "translate-x-0"
                    }`}
                  />
                  <button
                    data-testid="view-grid-btn"
                    onClick={() => setView("grid")}
                    className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-200 ${
                      view === "grid"
                        ? "text-[#1A1815] dark:text-white"
                        : "text-[#8B8680] dark:text-white/45"
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                  <button
                    data-testid="view-list-btn"
                    onClick={() => setView("list")}
                    className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-200 ${
                      view === "list"
                        ? "text-[#1A1815] dark:text-white"
                        : "text-[#8B8680] dark:text-white/45"
                    }`}
                  >
                    <List className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="category-filter-btn"
                      title="Filter by category"
                      className="flex h-11 items-center gap-2 rounded-full bg-[#F0EBE5] px-4 text-sm font-semibold text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    >
                      <SlidersHorizontal className="h-4 w-4" strokeWidth={1.9} />
                      <span className="hidden max-w-[120px] truncate sm:inline">
                        {categoryFilter === "all" ? "All Categories" : categoryFilter}
                      </span>
                      <ChevronDown className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                      data-testid="category-filter-all"
                      onSelect={() => setCategoryFilter("all")}
                      className={`gap-2 ${categoryFilter === "all" ? "font-semibold" : ""}`}
                    >
                      {categoryFilter === "all" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#12B76A]" />
                      ) : (
                        <span className="w-3.5" />
                      )}
                      All Categories
                    </DropdownMenuItem>
                    {CATEGORIES.map((cat) => (
                      <DropdownMenuItem
                        key={cat}
                        data-testid={`category-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                        onSelect={() => setCategoryFilter(cat)}
                        className={`gap-2 ${categoryFilter === cat ? "font-semibold" : ""}`}
                      >
                        {categoryFilter === cat ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#12B76A]" />
                        ) : (
                          <span className="w-3.5" />
                        )}
                        {cat}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Files count (reflects selected option) */}
            <div className="mt-6 flex items-end gap-2" data-testid="files-count">
              <span className="text-4xl font-bold tracking-tight text-[#1A1815] dark:text-white">
                {fileCount}
              </span>
              <span className="mb-1 text-sm text-[#8B8680] dark:text-white/45">
                {isTemplates ? `${activeLabel}` : `${activeLabel} files`}
              </span>
            </div>

            {/* Grid / List */}
            <div className="thin-scroll mt-6 flex-1 overflow-y-auto pr-1">
              <motion.div
                key={`${section}-${currentOption}-${view}`}
                initial="hidden"
                animate="show"
                variants={{ show: { transition: { staggerChildren: 0.055 } } }}
                className={`grid gap-4 ${
                  view === "grid"
                    ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
                    : "grid-cols-1"
                }`}
              >
                {filtered.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    doc={doc}
                    option={currentOption}
                    isTemplate={isTemplates}
                    onPreview={openDoc}
                    onVoid={setVoidTarget}
                    onMove={handleMove}
                    onHistory={handleHistory}
                    onDeleteTemplate={handleDeleteTemplate}
                  />
                ))}
              </motion.div>
              {filtered.length === 0 && (
                <p className="mt-10 text-center text-sm text-[#8B8680] dark:text-white/40">
                  No {activeLabel?.toLowerCase()} found.
                </p>
              )}
            </div>
          </>
        )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{isTemplates ? "Add template" : "Add document"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="doc-name">Name</Label>
              <Input
                id="doc-name"
                data-testid="doc-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isTemplates ? "e.g. Offer Letter" : "e.g. Employment Agreement"}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="doc-size">Size</Label>
                <Input
                  id="doc-size"
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-pages">Pages</Label>
                <Input
                  id="doc-pages"
                  type="number"
                  min="1"
                  value={form.pages}
                  onChange={(e) => setForm({ ...form, pages: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-type">Type</Label>
                <select
                  id="doc-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none"
                >
                  <option>PDF</option>
                  <option>DOCX</option>
                </select>
              </div>
            </div>

            {isTemplates ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tpl-owner">Owner</Label>
                  <select
                    id="tpl-owner"
                    data-testid="tpl-owner-select"
                    value={form.owner}
                    onChange={(e) => setForm({ ...form, owner: e.target.value })}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none"
                  >
                    <option value="me">My Templates</option>
                    <option value="shared">Shared With Me</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#1A1815] dark:text-white">
                    <input
                      type="checkbox"
                      data-testid="tpl-favorite-checkbox"
                      checked={form.favorite}
                      onChange={(e) => setForm({ ...form, favorite: e.target.checked })}
                      className="h-4 w-4 accent-[#F79009]"
                    />
                    Mark as favorite
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="doc-status">Status</Label>
                <select
                  id="doc-status"
                  data-testid="doc-status-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none"
                >
                  {OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <DialogFooter>
              <button
                type="submit"
                data-testid="submit-add-document"
                className="rounded-lg bg-[#1A1815] px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 dark:bg-white dark:text-black"
              >
                {isTemplates ? "Add template" : "Add document"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Envelope history / audit log */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="sm:max-w-[520px]" data-testid="history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Envelope history
            </DialogTitle>
          </DialogHeader>
          <div className="pt-1">
            <p className="mb-4 line-clamp-2 text-sm font-medium text-[#8B8680] dark:text-white/50">
              {history?.subject}
            </p>
            {historyLoading ? (
              <p className="py-8 text-center text-sm text-[#8B8680] dark:text-white/40">
                Loading history…
              </p>
            ) : history && history.events && history.events.length > 0 ? (
              <ol className="relative space-y-4 pl-1">
                {history.events.map((ev, i) => {
                  const Icon =
                    ev.type === "sent"
                      ? Send
                      : ev.type === "viewed"
                      ? User
                      : ev.type === "signed"
                      ? CheckCircle2
                      : ev.type === "voided"
                      ? Ban
                      : AlertCircle;
                  const tint =
                    ev.type === "signed"
                      ? "#12B76A"
                      : ev.type === "voided" || ev.type === "declined"
                      ? "#E5484D"
                      : ev.type === "sent"
                      ? "#7A6FF0"
                      : "#8B8680";
                  return (
                    <li
                      key={i}
                      data-testid={`history-event-${i}`}
                      className="flex items-start gap-3"
                    >
                      <span
                        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: `${tint}22`, color: tint }}
                      >
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <div className="flex min-w-0 flex-col">
                        <span className="text-sm font-semibold text-[#1A1815] dark:text-white">
                          {ev.text}
                        </span>
                        <span className="text-xs text-[#8B8680] dark:text-white/40">
                          {fmtStamp(ev.at) || "—"}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="py-8 text-center text-sm text-[#8B8680] dark:text-white/40">
                No history yet for this envelope.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Void confirmation */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => !o && setVoidTarget(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="void-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B42318]">
              <Ban className="h-5 w-5" />
              Void envelope
            </DialogTitle>
          </DialogHeader>
          <p className="pt-1 text-sm text-[#8B8680] dark:text-white/55">
            This cancels <span className="font-semibold text-[#1A1815] dark:text-white">"{voidTarget?.name}"</span> and
            stops any remaining recipients from signing. This action can't be undone.
          </p>
          <DialogFooter className="gap-2">
            <button
              data-testid="void-cancel-btn"
              onClick={() => setVoidTarget(null)}
              className="rounded-lg bg-[#F0EBE5] px-5 py-2.5 text-sm font-semibold text-[#1A1815] transition-transform hover:scale-[1.02] active:scale-95 dark:bg-white/10 dark:text-white"
            >
              Cancel
            </button>
            <button
              data-testid="void-confirm-btn"
              onClick={() => handleVoid(voidTarget)}
              className="rounded-lg bg-[#B42318] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95"
            >
              Void envelope
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnvelopeDocuments;